/**
 * SessionContainer — the wipeable encrypted object that turns the single-shot
 * transporter into a real bidirectional conversation.
 *
 * A conversation is TWO independent mono-directional lanes bound to ONE
 * authenticated handshake. Each endpoint sends on one lane and receives on the
 * other; every message is a Capsule keyed by that lane's symmetric deleting
 * ratchet. The container owns both ratchets, the sliding idle timer, and the
 * single erasure point (`destroy`). Manual destruct and idle-lapse both land on
 * that one erasure — this is what the UI "destruct" button targets.
 *
 * Security rests on the seed (R, split 2-of-2 across two independent OOB
 * channels), inherited via `deriveKeys` (all S2/I1 guards apply). Lane roots are
 * domain-separated so an a2b ciphertext can never verify under a b2a key.
 * Nothing here touches @fume/core; capsules ship as fragments over whatever
 * transport, and the ratchet chains never leave the device.
 */
import { deriveKeys, hkdfSha256 } from '@fume/core';
import { Ratchet } from './ratchet.js';
import { openCapsule, sealCapsule } from './capsule.js';
const utf8 = new TextEncoder();
const KEY_SIZE = 32;
const SESSION_ID_SIZE = 32;
const realScheduler = {
    set: (delayMs, cb) => setTimeout(cb, delayMs),
    clear: (handle) => clearTimeout(handle),
};
function laneRoot(base, lane) {
    return hkdfSha256(base, null, utf8.encode(`fume/session/lane/v1|${lane}`), KEY_SIZE);
}
export class SessionContainer {
    role;
    sendLaneId;
    recvLaneId;
    sendRatchet;
    recvRatchet;
    transcriptHash;
    sessionId;
    idleTtlSeconds;
    activeIdleWipe;
    scheduler;
    lastActivityAt;
    idleHandle = undefined;
    destroyed = false;
    constructor(options) {
        if (!(options.sessionId instanceof Uint8Array) ||
            options.sessionId.length !== SESSION_ID_SIZE) {
            throw new RangeError('sessionId must be exactly 32 bytes');
        }
        if (!Number.isInteger(options.idleTtlSeconds) || options.idleTtlSeconds <= 0) {
            throw new RangeError('idleTtlSeconds must be a positive integer');
        }
        if (!Number.isInteger(options.createdAt) || options.createdAt < 0) {
            throw new RangeError('createdAt must be a non-negative integer');
        }
        // Inherit every S2/I1 guard: rejects an unbound transcript, dh === R,
        // sasVerified !== true, and wrong buffer sizes.
        const base = deriveKeys(options.R, options.dh, options.transcript);
        const a2bRoot = laneRoot(base.contentKey, 'a2b');
        const b2aRoot = laneRoot(base.contentKey, 'b2a');
        base.contentKey.fill(0);
        base.manifestKey.fill(0);
        base.receiptKey.fill(0);
        this.role = options.role;
        this.sendLaneId = options.role === 'initiator' ? 'a2b' : 'b2a';
        this.recvLaneId = options.role === 'initiator' ? 'b2a' : 'a2b';
        this.sendRatchet = new Ratchet(this.sendLaneId === 'a2b' ? a2bRoot : b2aRoot);
        this.recvRatchet = new Ratchet(this.recvLaneId === 'a2b' ? a2bRoot : b2aRoot);
        // The ratchets copied the roots; wipe our local copies.
        a2bRoot.fill(0);
        b2aRoot.fill(0);
        this.transcriptHash = options.transcript.hash.slice();
        this.sessionId = options.sessionId.slice();
        this.idleTtlSeconds = options.idleTtlSeconds;
        this.activeIdleWipe = options.activeIdleWipe ?? true;
        this.scheduler = options.scheduler ?? realScheduler;
        this.lastActivityAt = options.createdAt;
        this.armIdleTimer();
    }
    get isDestroyed() {
        return this.destroyed;
    }
    get sendLane() {
        return this.sendLaneId;
    }
    get recvLane() {
        return this.recvLaneId;
    }
    /** True once `now` has reached the sliding idle deadline. */
    isIdleExpired(now) {
        return now >= this.lastActivityAt + this.idleTtlSeconds;
    }
    /** Seal the next outbound message into a capsule on this endpoint's send lane. */
    send(plaintext, now) {
        this.assertLive(now);
        const step = this.sendRatchet.next();
        try {
            const capsule = sealCapsule(plaintext, step.key, this.sendLaneId, step.seq, this.transcriptHash, this.sessionId);
            this.touch(now);
            return capsule;
        }
        finally {
            step.key.fill(0); // per-message key erased immediately after use
        }
    }
    /** Open an inbound capsule from the peer's send lane, returning the plaintext. */
    receive(capsule, now) {
        this.assertLive(now);
        if (capsule.lane !== this.recvLaneId) {
            throw new Error(`capsule lane ${capsule.lane} is not this endpoint's receive lane ${this.recvLaneId}`);
        }
        if (capsule.seq < this.recvRatchet.seq) {
            throw new Error(`capsule seq ${capsule.seq} already consumed (replay or out-of-order past)`);
        }
        this.recvRatchet.advanceTo(capsule.seq);
        const step = this.recvRatchet.next();
        try {
            const plaintext = openCapsule(capsule, step.key, this.transcriptHash, this.sessionId);
            this.touch(now);
            return plaintext;
        }
        finally {
            step.key.fill(0);
        }
    }
    /**
     * Single erasure point. Zero-fills both lane ratchets and cancels the idle
     * timer. Idempotent. This is where both the manual "destruct" button and the
     * idle-lapse timer land.
     */
    destroy() {
        if (this.destroyed)
            return;
        this.destroyed = true;
        this.sendRatchet.destroy();
        this.recvRatchet.destroy();
        this.transcriptHash.fill(0);
        if (this.idleHandle !== undefined) {
            this.scheduler.clear(this.idleHandle);
            this.idleHandle = undefined;
        }
    }
    assertLive(now) {
        if (this.destroyed) {
            throw new Error('session container is destroyed');
        }
        if (!Number.isInteger(now) || now < 0) {
            throw new RangeError('now must be a non-negative integer');
        }
        if (this.isIdleExpired(now)) {
            this.destroy();
            throw new Error('session container idle-expired');
        }
    }
    touch(now) {
        this.lastActivityAt = now;
        this.armIdleTimer();
    }
    armIdleTimer() {
        if (!this.activeIdleWipe)
            return;
        if (this.idleHandle !== undefined) {
            this.scheduler.clear(this.idleHandle);
            this.idleHandle = undefined;
        }
        if (this.destroyed)
            return;
        this.idleHandle = this.scheduler.set(this.idleTtlSeconds * 1000, () => {
            this.destroy();
        });
    }
}
//# sourceMappingURL=container.js.map